import React, { useState, useRef } from "react";
import { useFetch } from "@/hooks/useApi";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Loader, Plate, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { gbp, num, ukDate } from "@/lib/format";
import * as Icons from "lucide-react";
import { toast } from "sonner";

export default function DpdImport() {
  const { data: payments, loading, refetch } = useFetch("/dpd/payments");
  const { data: routeMap, refetch: refetchMap } = useFetch("/route-map");
  const { data: vans } = useFetch("/vans");
  const [newRoute, setNewRoute] = useState("");
  const [newVan, setNewVan] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [fd, setFd] = useState("");
  const inputRef = useRef();

  const upload = async () => {
    if (!file) { toast.error("Choose a DPD CSV file first"); return; }
    setBusy(true);
    try {
      const fd_ = new FormData();
      fd_.append("file", file);
      const { data } = await api.post("/import/dpd", fd_, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.rows} rows · ${gbp(data.total_payment)} revenue`);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      refetch();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  };

  const saveMap = async () => {
    if (!newRoute || !newVan) { toast.error("Enter a route and pick a van"); return; }
    try {
      await api.post("/route-map", { route: newRoute.trim(), van_reg: newVan });
      toast.success("Route mapped");
      setNewRoute(""); setNewVan("");
      refetchMap();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delMap = async (route) => {
    try { await api.delete(`/route-map/${encodeURIComponent(route)}`); refetchMap(); }
    catch { toast.error("Error"); }
  };

  const rows = (payments || []).filter((p) => !fd || (p.fd || "").toLowerCase().includes(fd.toLowerCase()));
  const totalRev = rows.reduce((a, p) => a + (p.total || 0), 0);

  return (
    <div className="fp-fade-in">
      <PageHeader title="DPD Import" subtitle="Upload your DPD Payments CSV to load daily stops, drivers & revenue" testId="dpd-import-page" />

      <Card className="p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium mb-1.5 block">DPD Payments CSV</label>
            <Input ref={inputRef} type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files[0])} data-testid="dpd-file-input" />
            <p className="text-xs text-muted-foreground mt-1.5">Export from myportal.drivers.dpd.co.uk → Payments → download CSV. Expected columns: FD Number, Date, No of Stops, Total Parcels, Excess Parcels, Stop, Parcels, Total, Route, Mileage, Driver.</p>
          </div>
          <Button onClick={upload} disabled={busy} className="w-full sm:w-auto" data-testid="dpd-import-button">
            {busy ? <Icons.LoaderCircle className="h-4 w-4 animate-spin" /> : <><Icons.Upload className="h-4 w-4 mr-1.5" /> Import</>}
          </Button>
        </div>

        {result && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6" data-testid="dpd-import-result">
            <div className="rounded-lg bg-muted p-3"><p className="text-xs text-muted-foreground">Rows imported</p><p className="font-mono text-lg font-bold">{num(result.rows)}</p></div>
            <div className="rounded-lg bg-muted p-3"><p className="text-xs text-muted-foreground">New drivers</p><p className="font-mono text-lg font-bold">{num(result.drivers_created)}</p></div>
            <div className="rounded-lg bg-muted p-3"><p className="text-xs text-muted-foreground">Mileage rows</p><p className="font-mono text-lg font-bold">{num(result.mileage_created)}</p></div>
            <div className="rounded-lg bg-muted p-3"><p className="text-xs text-muted-foreground">Revenue added</p><p className="font-mono text-lg font-bold text-emerald-400">{gbp(result.total_payment)}</p></div>
            <div className="rounded-lg bg-muted p-3"><p className="text-xs text-muted-foreground">Date range</p><p className="font-mono text-sm font-bold">{result.date_from ? `${ukDate(result.date_from)} – ${ukDate(result.date_to)}` : "—"}</p></div>
          </div>
        )}
        {result && result.unmapped_routes?.length > 0 && (
          <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-500" data-testid="unmapped-warning">
            <Icons.AlertTriangle className="h-4 w-4 inline mr-1.5" />
            {result.unmapped_routes.length} route(s) not linked to a van: <span className="font-mono font-semibold">{result.unmapped_routes.join(", ")}</span>. Map them below, then re-import to attach mileage & vans.
          </div>
        )}
      </Card>

      <Card className="p-6 mb-6" data-testid="route-map-card">
        <div className="flex items-center gap-2 mb-1">
          <Icons.Route className="h-4 w-4 text-primary" />
          <h3 className="font-display font-semibold">Route → Van Mapping</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Link DPD route numbers to your vans so imported stops and daily mileage attach to the right vehicle — and fuel/costs line up per van.</p>
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <Input placeholder="Route number (e.g. 208)" value={newRoute} onChange={(e) => setNewRoute(e.target.value)} className="font-mono sm:w-48" data-testid="route-input" />
          <Select value={newVan} onValueChange={setNewVan}>
            <SelectTrigger className="sm:w-64" data-testid="route-van-select"><SelectValue placeholder="Select van" /></SelectTrigger>
            <SelectContent>{(vans || []).map((v) => <SelectItem key={v.id} value={v.registration}>{v.registration} · {v.make_model}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={saveMap} className="w-full sm:w-auto" data-testid="add-route-map-button"><Icons.Plus className="h-4 w-4 mr-1.5" /> Map</Button>
        </div>
        {(routeMap || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No routes mapped yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {routeMap.map((m) => (
              <div key={m.route} className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5" data-testid={`route-map-${m.route}`}>
                <span className="font-mono text-sm">Route {m.route}</span>
                <Icons.ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                <Plate reg={m.van_reg} />
                <button onClick={() => delMap(m.route)} className="ml-1 text-muted-foreground hover:text-rose-400" data-testid={`del-route-map-${m.route}`}><Icons.X className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
        <h3 className="font-display font-semibold">Imported Payments {rows.length > 0 && <span className="text-muted-foreground font-normal">· {gbp(totalRev)}</span>}</h3>
        <div className="relative w-full sm:w-56">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Filter by FD number" value={fd} onChange={(e) => setFd(e.target.value)} className="pl-9 w-full font-mono" data-testid="dpd-fd-filter" />
        </div>
      </div>

      <Card className="overflow-hidden">
        {loading ? <Loader /> : rows.length === 0 ? (
          <EmptyState icon="Download" title="No DPD payments yet" subtitle="Upload your DPD CSV above to get started." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>FD</TableHead><TableHead>Date</TableHead><TableHead>Driver</TableHead>
                <TableHead>Stops</TableHead><TableHead>Parcels</TableHead><TableHead>Excess</TableHead>
                <TableHead>Route</TableHead><TableHead>Miles</TableHead><TableHead>Total</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((p) => (
                  <TableRow key={p.key} data-testid={`dpd-row-${p.key}`}>
                    <TableCell className="font-mono text-sm">{p.fd}</TableCell>
                    <TableCell className="font-mono text-sm">{ukDate(p.date)}</TableCell>
                    <TableCell><p className="font-medium">{p.driver_name}</p><p className="text-xs text-muted-foreground font-mono">{p.driver_code}</p></TableCell>
                    <TableCell className="font-mono">{num(p.stops)}</TableCell>
                    <TableCell className="font-mono">{num(p.total_parcels)}</TableCell>
                    <TableCell className="font-mono text-muted-foreground">{num(p.excess_parcels)}</TableCell>
                    <TableCell className="font-mono">{p.route || "—"}</TableCell>
                    <TableCell className="font-mono">{num(p.mileage)}</TableCell>
                    <TableCell className="font-mono font-semibold text-emerald-400">{gbp(p.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
